import { useState, useRef } from 'react';
import { FiX, FiUpload, FiLink, FiLoader, FiEdit3 } from 'react-icons/fi';
import toast from 'react-hot-toast';

export default function SalesContentModal({ onClose, portalId }) {
  const [contentUrl, setContentUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('url'); // 'url' | 'file' | 'text'
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [contentType, setContentType] = useState('case study');
  const [textContent, setTextContent] = useState('');
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  // Drag and drop
  const handleDragEnter = (e) => { e.preventDefault(); setIsDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragActive(false); };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Validation
  const validateForm = () => {
    if (uploadMode === 'url') {
      if (!contentUrl.trim()) {
        setError('Please enter a valid URL');
        return false;
      }
      try { new URL(contentUrl); } catch {
        setError('Please enter a valid URL');
        return false;
      }
    } else if (uploadMode === 'file') {
      if (!selectedFile) {
        setError('Please select a file');
        return false;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File must be less than 10MB');
        return false;
      }
    } else if (uploadMode === 'text') {
      if (!textContent.trim()) {
        setError('Please enter some text');
        return false;
      }
    }
    return true;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;

    if (!portalId) {
      toast.error("No HubSpot portal selected.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (uploadMode === 'url') {
        const urlObj = new URL(contentUrl.trim());
        const fileName = urlObj.pathname.split('/').pop() || 'link.pdf';

        const submissionData = {
          type: 'url',
          url: contentUrl.trim(),
          fileName,
          portalId,
        };

        fetch('http://localhost:8080/content', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionData),
        }).catch(() => {});
      }

      else if (uploadMode === 'file') {
        const formData = new FormData();
        formData.append('type', 'file');
        formData.append('portalId', portalId);
        formData.append('file', selectedFile);

        fetch('http://localhost:8080/content', {
          method: 'POST',
          body: formData,
        }).catch(() => {});
      }

      else if (uploadMode === 'text') {
        const submissionData = {
          type: 'text',
          text: textContent.trim(),
          fileName: 'copy.txt', // could make this user-editable
          portalId,
        };

        console.log('>>> Submission Data:', submissionData);

       
        fetch('http://localhost:8080/content', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionData),
        }).catch(() => {});
       


      }

      toast.success('✅ Content submitted! Processing has started.');
      onClose();
    } catch (err) {
      toast.error(err.message || 'An error occurred while submitting your content.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 rounded-t-xl flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">Add Sales Content</h2>
            <p className="text-sm text-gray-600">Choose how you want to provide content</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600">{error}</div>}

          {/* Content Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Content Type</label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              <option value="case study">Case Study</option>
              <option value="testimonial">Testimonial</option>
              <option value="one-sheeter">One-Sheeter</option>
              <option value="blog">Blog</option>
              <option value="webinar">Webinar</option>
              <option value="white paper">White Paper</option>
            </select>
          </div>

          {/* Input Mode Selector */}
          <div>
            <label className="block text-sm font-medium mb-2">Input Method</label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input type="radio" value="url" checked={uploadMode === 'url'} onChange={() => setUploadMode('url')} />
                <span>URL</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" value="file" checked={uploadMode === 'file'} onChange={() => setUploadMode('file')} />
                <span>File Upload</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" value="text" checked={uploadMode === 'text'} onChange={() => setUploadMode('text')} />
                <span>Text</span>
              </label>
            </div>
          </div>

          {/* URL Input */}
          {uploadMode === 'url' && (
            <div>
              <label className="flex items-center mb-1 text-sm font-medium"><FiLink className="mr-2" /> Content URL</label>
              <input
                type="url"
                value={contentUrl}
                onChange={(e) => setContentUrl(e.target.value)}
                placeholder="https://example.com/document.pdf"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* File Upload */}
          {uploadMode === 'file' && (
            <div>
              <label className="flex items-center mb-1 text-sm font-medium"><FiUpload className="mr-2" /> Upload File</label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center ${
                  isDragActive ? 'border-blue-400 bg-blue-50' :
                  selectedFile ? 'border-green-400 bg-green-50' : 'border-gray-300'
                }`}
                onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}
                onDragOver={handleDragOver} onDrop={handleDrop}
              >
                {selectedFile ? (
                  <p>{selectedFile.name}</p>
                ) : (
                  <p className="text-sm text-gray-600">Drag and drop a file here, or <button type="button" onClick={() => fileInputRef.current?.click()} className="text-blue-600 underline">browse</button></p>
                )}
                <input ref={fileInputRef} type="file" onChange={handleFileInputChange} className="absolute inset-0 opacity-0 cursor-pointer" />
              </div>
            </div>
          )}

          {/* Text Input */}
          {uploadMode === 'text' && (
            <div>
              <label className="flex items-center mb-1 text-sm font-medium"><FiEdit3 className="mr-2" /> Enter Text</label>
              <textarea
                rows={5}
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                placeholder="Paste or type text here..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
              />
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end space-x-3">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg" disabled={isSubmitting}>Cancel</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            {isSubmitting ? (<><FiLoader className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>) : 'Create Sales Content'}
          </button>
        </div>
      </div>
    </div>
  );
}


